<template>
    <div
        class="flex flex-col flex-1 h-full min-h-0 overflow-visible"
        v-if="selectedConversation?.id"
    >
        <sms-chat-conversation-header
            :show-conversation-details="showConversationDetails"
            @toggle-show-conversation-details="emit('toggle-show-conversation-details', $event)"
            v-if="!hideHeader"
        />

        <!-- Messages -->
        <div
            class="flex-1 min-h-0 p-4 overflow-y-auto overscroll-contain bg-gray-200 flex flex-col"
            ref="messagesContainer"
        >
            <infinite-loading
                class="text-center"
                :class="{
                    'h-full flex items-center justify-center': !selectedConversationMessages.length,
                }"
                :top="true"
                :distance="300"
                :identifier="messagesFetchId"
                @infinite="onInfinite"
            >
                <template #spinner>
                    <div class="flex justify-center px-4 pb-0 pt-8">
                        <activix-spinner class="h-10 w-10" />
                    </div>
                </template>

                <template #error="{ retry }">
                    <div class="flex flex-col items-center gap-3 p-4 text-gray-600">
                        <span>{{ t('smsCommunication.pagination.paused') }}</span>
                        <activix-button
                            data-testid="resume-messages-pagination"
                            :size="ButtonSize.SM"
                            @click="retry"
                        >
                            {{ t('smsCommunication.pagination.loadMore') }}
                        </activix-button>
                    </div>
                </template>

                <template #complete>
                    <span />
                </template>
            </infinite-loading>

            <sms-unread-messages-separator
                class="sticky top-0 z-10"
                :clickable="!isSeeking"
                :loading="isSeeking"
                :show-scroll-hint="!isSeeking"
                data-testid="sticky-unread-separator"
                @click="onStickyUnreadSeparatorClick"
                v-if="shouldShowStickyUnreadSeparator && hadHistoricalUnreadAtLoad"
            />

            <sms-conversation-message-group
                :highlighted-communication-id="highlightedCommunicationId"
                :highlighted-message-ids="highlightedMessageIds"
                :messages="group.messages"
                :lead-name="selectedConversationDisplayLead?.fullName || ''"
                :first-unread-message-id="firstUnreadMessageId"
                :scroll-root="messagesContainer"
                :scroll-to-unread-separator-request-id="scrollToUnreadSeparatorSignal"
                :is-read-only="!canCompose"
                :retrying-message-ids="retryingMessageIds"
                :key="group.date"
                @highlight-animation-end="onHighlightAnimationEnd"
                @unread-separator-visibility-change="onUnreadSeparatorVisibilityChange"
                @retry="onRetryMessage"
                v-for="group in groupedMessages"
            />

            <!-- New messages indicator -->
            <transition name="new-messages">
                <sms-new-messages-indicator
                    class="sticky bottom-4 z-10"
                    :message-count="newMessageIndicator.newMessageCount.value"
                    data-testid="new-messages-indicator"
                    @click="onNewMessagesIndicatorClick"
                    v-if="newMessageIndicator.shouldShowNewMessageIndicator.value"
                />
            </transition>

            <div class="h-px shrink-0" ref="bottomSentinel" />
        </div>

        <!-- Communicating warning -->
        <lead-communicating-banner
            :communicating-lead="communicating.communicatingLead.value"
            :recent-communication="communicating.recentCommunication.value"
            v-if="
                canCompose &&
                (communicating.isConflict.value || communicating.isRecentCommunication.value)
            "
        />

        <div
            class="p-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 text-center"
            data-testid="read-only-banner"
            v-if="selectedConversationReadOnly"
        >
            {{ t('smsCommunication.readOnlyConversation') }}
        </div>

        <sms-unsubscribed-panel
            :phone-number="selectedConversation?.smsProvider?.phone || ''"
            v-else-if="currentLead?.isUnsubscribedToSms"
        />

        <sms-consent-expired-panel v-else-if="currentLead?.isCaslExpired" />

        <div
            class="p-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 text-center"
            data-testid="send-disabled-banner"
            v-else-if="!selectedConversationCanSend"
        >
            {{ sendDisabledMessage }}
        </div>

        <!-- Input -->
        <conversation-message-input
            :has-ai-draft="hasAiDraft"
            :is-service-lead="isServiceLead"
            :disabled="communicating.composerDisabled.value"
            @write-intent="communicating.claimOnWriteIntent()"
            @send-success="communicating.release()"
            @before-resize="onBeforeInputResize"
            @resize="onInputResize"
            v-else-if="!selectedConversation?.archived"
        />
    </div>
</template>

<script setup lang="ts">
    import {
        ref,
        computed,
        watch,
        nextTick,
        onMounted,
        onBeforeUnmount,
        onBeforeUpdate,
        onUpdated,
    } from 'vue';
    import { storeToRefs } from 'pinia';
    import InfiniteLoading from 'v3-infinite-loading';
    import 'v3-infinite-loading/lib/style.css';
    import ActivixSpinner from '@/components/activix-ui/ActivixSpinner/ActivixSpinner.vue';
    import ActivixButton from '@/components/activix-ui/ActivixButton/ActivixButton.vue';
    import { ButtonSize } from '@/components/activix-ui/ActivixButton/types.ts';
    import ActivixDate from '@/value-objects/ActivixDate.js';
    import { useI18n } from '@/hooks/useI18n.ts';
    import { useNotify } from '@/hooks/useNotify.ts';
    import { useNiotextStore } from '@/store/modules/niotext/store.ts';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { useSmsDrawer } from '@/components/sms/hooks/useSmsDrawer.ts';
    import { FetchStatus } from '@/store/modules/niotext/FetchStatus.ts';
    import ConversationMessageInput from '@/components/sms/ConversationMessageInput.vue';
    import SmsChatConversationHeader from '@/components/sms/SmsChatConversationHeader.vue';
    import SmsConversationMessageGroup from '@/components/sms/SmsConversationMessageGroup.vue';
    import { adjustScrollAfterImages } from '@/components/sms/utils/adjustScrollAfterImages.ts';
    import SmsUnreadMessagesSeparator from '@/components/sms/SmsUnreadMessagesSeparator.vue';
    import SmsUnsubscribedPanel from '@/components/sms/SmsUnsubscribedPanel.vue';
    import SmsConsentExpiredPanel from '@/components/sms/SmsConsentExpiredPanel.vue';
    import { useSmsCommunicating } from '@/components/sms/hooks/useSmsCommunicating.ts';
    import {
        SMS_SCROLL_SETTLE_DELAY,
        useSmsUnreadBoundary,
    } from '@/components/sms/hooks/useSmsUnreadBoundary.ts';
    import LeadCommunicatingBanner from '@/components/sms/LeadCommunicatingBanner.vue';
    import SmsNewMessagesIndicator from '@/components/sms/SmsNewMessagesIndicator.vue';
    import { useSmsNewMessageIndicator } from '@/components/sms/hooks/useSmsNewMessageIndicator.ts';
    import Division from '@/entities/Division.js';

    interface Props {
        showConversationDetails: boolean;
        hideHeader?: boolean;
    }

    interface Emits {
        (e: 'toggle-show-conversation-details', value: boolean): void;
    }

    defineProps<Props>();

    const emit = defineEmits<Emits>();

    const { t } = useI18n();
    const { notify } = useNotify();

    const niotextStore = useNiotextStore();
    const accountStore = useAccountStore();
    const {
        selectedConversation,
        selectedConversationDisplayLead,
        selectedConversationKind,
        selectedConversationMessages,
        selectedConversationLastMessageReadIdAtLoad,
        selectedConversationReadOnly,
        selectedConversationCanSend,
        selectedConversationSendDisabledReason,
        messagesFetchId,
        hasFetchedAllMessages,
        currentLead,
    } = storeToRefs(niotextStore);

    const { targetCommunicationId } = useSmsDrawer();

    const communicating = useSmsCommunicating();

    type AiDraftAccount = {
        id: number;
        children?: AiDraftAccount[];
        hasAiDraft?: () => boolean;
    };

    const aiDraftAccount = computed<AiDraftAccount | null>(() => {
        const leadAccountId = currentLead.value?.accountId;
        const contextAccount = accountStore.contextAccount as AiDraftAccount | null | undefined;

        if (!leadAccountId || !contextAccount) {
            return null;
        }

        if (contextAccount.id === leadAccountId) {
            return contextAccount;
        }

        return contextAccount.children?.find((account) => account.id === leadAccountId) ?? null;
    });

    const hasAiDraft = computed<boolean>(() => {
        return Boolean(aiDraftAccount.value?.hasAiDraft?.());
    });

    const isServiceLead = computed<boolean>(() => {
        return currentLead.value?.divisionId === Division.SERVICE;
    });

    const canCompose = computed(() => {
        return (
            !selectedConversationReadOnly.value &&
            selectedConversationCanSend.value &&
            !currentLead.value?.isUnsubscribedToSms &&
            !currentLead.value?.isCaslExpired &&
            !selectedConversation.value?.archived
        );
    });

    const sendDisabledMessage = computed(() => {
        if (selectedConversationSendDisabledReason.value === 'sms_provider_inactive') {
            return t('smsCommunication.smsProviderInactive');
        }

        return t('smsCommunication.sendUnavailable');
    });

    const groupedMessages = computed(() => {
        const items = selectedConversationMessages.value || [];
        const groupsByDate = new Map<string, typeof items>();

        for (const [index, message] of items.entries()) {
            const dateKey = message.createdAt
                ? new ActivixDate(message.createdAt).startOfDay().toDateString()
                : `unknown-${index}`;

            if (!groupsByDate.has(dateKey)) {
                groupsByDate.set(dateKey, []);
            }
            groupsByDate.get(dateKey)!.push(message);
        }

        return Array.from(groupsByDate, ([date, messages]) => ({ date, messages }));
    });

    const messagesContainer = ref<HTMLElement>();
    const initialLoading = ref<boolean>(true);
    const wasUserAtBottom = ref<boolean>(true);
    const shouldScrollOnNextUpdate = ref<boolean>(false);
    const highlightedCommunicationId = ref<number | null>(null);
    const isUserAtBottom = ref<boolean>(true);
    const highlightedMessageIds = ref<Set<number>>(new Set());
    const retryingMessageIds = ref<Set<number>>(new Set());
    const hasScrolledToTarget = ref<boolean>(false);
    const hadHistoricalUnreadAtLoad = ref(false);
    let previousLastMessageIdBeforeUpdate: number | null = null;

    const scrollToTopToLoadUnreadBoundary = (): void => {
        if (!messagesContainer.value) {
            return;
        }

        messagesContainer.value.scrollTop = 0;
    };

    const {
        firstUnreadMessageId,
        hasSeenUnreadSeparator,
        isLastReadMessageOutsideLoadedRange,
        shouldShowStickyUnreadSeparator,
        scrollToUnreadSeparatorSignal,
        onStickyUnreadSeparatorClick,
        onUnreadSeparatorVisibilityChange,
        isSeeking,
    } = useSmsUnreadBoundary({
        selectedConversation,
        selectedConversationMessages,
        selectedConversationLastMessageReadIdAtLoad,
        hasFetchedAllMessages,
        initialLoading,
        scrollToTopToLoadUnreadBoundary,
        seekMessageInHistory: niotextStore.seekMessageInHistory,
    });

    const lastMessage = computed(
        () => selectedConversationMessages.value[selectedConversationMessages.value.length - 1]
    );

    const isDraftConversation = computed<boolean>(
        () => !selectedConversation.value || selectedConversation.value.id < 0
    );

    const scrollContainerToBottom = (el: HTMLElement) => {
        el.scrollTop = el.scrollHeight;
    };

    const BOTTOM_THRESHOLD = 24;

    const getIsUserAtBottom = () => {
        const el = messagesContainer.value;

        if (!el) {
            return true;
        }

        return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD;
    };

    const bottomSentinel = ref<HTMLElement>();
    let bottomVisibilityObserver: IntersectionObserver | null = null;
    let wasBottomSentinelVisible = false;

    // When the messages container height shrinks (e.g. window resize, devtools
    // opening), the scroll position stays the same but the last message may be
    // pushed below the visible area. Track the container height and re-pin to
    // bottom when the user was already there before the shrink.
    let lastContainerHeight = 0;
    let lastScrollHeight = 0;
    let containerResizeObserver: ResizeObserver | null = null;

    const setupBottomVisibilityObserver = (): void => {
        cleanupBottomVisibilityObserver();

        if (!messagesContainer.value || !bottomSentinel.value) {
            return;
        }

        bottomVisibilityObserver = new IntersectionObserver(
            (entries) => {
                const isVisible = !!entries[0]?.isIntersecting;
                const justBecameVisible = isVisible && !wasBottomSentinelVisible;
                wasBottomSentinelVisible = isVisible;
                isUserAtBottom.value = isVisible;

                if (justBecameVisible) {
                    niotextStore.syncSelectedConversationReadState();
                    newMessageIndicator.dismiss();
                }
            },
            { root: messagesContainer.value, threshold: 0 }
        );

        bottomVisibilityObserver.observe(bottomSentinel.value);
    };

    const cleanupBottomVisibilityObserver = (): void => {
        bottomVisibilityObserver?.disconnect();
        bottomVisibilityObserver = null;
        wasBottomSentinelVisible = false;
    };

    const scrollToBottom = () => {
        initialLoading.value = false;

        nextTick(() => {
            const el = messagesContainer.value;

            if (!el) {
                return;
            }

            scrollContainerToBottom(el);
            adjustScrollAfterImages(el, scrollContainerToBottom);
        });
    };

    const newMessageIndicator = useSmsNewMessageIndicator({
        selectedConversation,
        selectedConversationMessages,
        messagesContainer,
        scrollToBottom,
    });

    const scrollToInitialPosition = (): void => {
        hadHistoricalUnreadAtLoad.value =
            !!firstUnreadMessageId.value || isLastReadMessageOutsideLoadedRange.value;

        if (!firstUnreadMessageId.value || hasSeenUnreadSeparator.value) {
            scrollToBottom();
            return;
        }

        initialLoading.value = false;

        nextTick(() => {
            scrollToUnreadSeparatorSignal.value++;
        });
    };

    const scrollToTargetMessage = (communicationId: number): void => {
        // Use setTimeout to let infinite loading settle before scrolling
        // 300ms allows infinite-loading's scroll position adjustments to complete
        setTimeout(() => {
            const container = messagesContainer.value;
            if (!container) {
                return;
            }

            const targetElement = container.querySelector(
                `[data-communication-id="${communicationId}"]`
            ) as HTMLElement | null;

            if (!targetElement) {
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const targetRect = targetElement.getBoundingClientRect();

            // Calculate target's position within scroll content:
            // targetRect.top is viewport-relative, convert to scroll-content-relative
            // by adding current scrollTop and subtracting container's viewport position
            const targetPositionInContent =
                targetRect.top - containerRect.top + container.scrollTop;

            const containerHeight = container.clientHeight;
            const targetHeight = targetElement.offsetHeight;
            const maxScroll = container.scrollHeight - containerHeight;

            // Calculate scroll position to center target in container
            const idealScrollTop = targetPositionInContent - (containerHeight - targetHeight) / 2;

            // Clamp to valid scroll range
            const scrollTop = Math.max(0, Math.min(idealScrollTop, maxScroll));

            // Use 'instant' behavior to avoid race conditions with infinite-loading's
            // scroll position adjustments during smooth scroll animation
            container.scrollTo({
                top: scrollTop,
                behavior: 'instant',
            });

            highlightedCommunicationId.value = communicationId;
            niotextStore.activateDeferredCursor();
        }, SMS_SCROLL_SETTLE_DELAY);
    };

    const HIGHLIGHT_DURATION = 2000;
    let highlightTimer: ReturnType<typeof setTimeout> | null = null;

    const clearHighlightTimer = (): void => {
        if (highlightTimer !== null) {
            clearTimeout(highlightTimer);
            highlightTimer = null;
        }
    };

    const onRetryMessage = async (messageId: number): Promise<void> => {
        if (retryingMessageIds.value.has(messageId)) {
            return;
        }

        retryingMessageIds.value = new Set(retryingMessageIds.value).add(messageId);

        try {
            await niotextStore.retryMessage(messageId);
        } catch {
            notify.error(t('smsCommunication.message.retryFailed'));
        } finally {
            const nextRetryingMessageIds = new Set(retryingMessageIds.value);
            nextRetryingMessageIds.delete(messageId);
            retryingMessageIds.value = nextRetryingMessageIds;
        }
    };

    const onHighlightAnimationEnd = (): void => {
        highlightedCommunicationId.value = null;
    };

    // When the input grows (e.g. textarea auto-grow on long messages, or
    // attachments preview added), the messages container shrinks. If the user
    // was already at the bottom, keep them pinned so the latest message stays
    // visible instead of getting hidden behind the input.
    // before-resize fires before the DOM mutation so getIsUserAtBottom()
    // reads pre-change geometry; resize fires after.
    let activeInputResizeCount = 0;
    let wasAtBottomBeforeInputResize = false;

    const onBeforeInputResize = (): void => {
        if (activeInputResizeCount === 0) {
            wasAtBottomBeforeInputResize = getIsUserAtBottom();
        }

        activeInputResizeCount += 1;
    };

    const onInputResize = (): void => {
        activeInputResizeCount = Math.max(0, activeInputResizeCount - 1);
        const shouldScrollToBottom = activeInputResizeCount === 0 && wasAtBottomBeforeInputResize;

        if (activeInputResizeCount === 0) {
            wasAtBottomBeforeInputResize = false;
        }

        if (initialLoading.value) {
            return;
        }

        if (shouldScrollToBottom) {
            scrollToBottom();
        }
    };

    const onNewMessagesIndicatorClick = (): void => {
        clearHighlightTimer();
        highlightedMessageIds.value = new Set(newMessageIndicator.newMessageIds.value);
        newMessageIndicator.scrollToNewMessages();

        highlightTimer = setTimeout(() => {
            highlightedMessageIds.value = new Set();
            highlightTimer = null;
        }, HIGHLIGHT_DURATION);
    };

    const onInfinite = async ($state: any) => {
        if (isSeeking.value) {
            $state.loaded();
            return;
        }

        if (isDraftConversation.value) {
            $state.complete();
            return;
        }

        if (hasFetchedAllMessages.value) {
            $state.complete();

            if (initialLoading.value && !targetCommunicationId.value) {
                scrollToInitialPosition();
            }

            return;
        }

        try {
            const fetchStatus = await niotextStore.fetchConversationMessages();

            switch (fetchStatus) {
                case FetchStatus.SUCCESS:
                    $state.loaded();
                    break;
                case FetchStatus.NO_MORE_RESULTS:
                case FetchStatus.NO_CONVERSATION_SELECTED:
                    $state.complete();
                    break;
                case FetchStatus.PAUSED:
                    // Reuse the loader's retry state without re-emitting against an unmoved sentinel.
                    $state.error();
                    break;
                case FetchStatus.CANCELED:
                    break;
            }

            if (initialLoading.value && !targetCommunicationId.value) {
                scrollToInitialPosition();
            }
        } catch {
            notify.error(t('smsCommunication.messagesList.failedToLoad'));

            $state.complete();
        }
    };

    watch(
        () => selectedConversation.value?.identityKey,
        () => {
            if (!selectedConversation.value?.id) {
                return;
            }

            initialLoading.value = true;
            hadHistoricalUnreadAtLoad.value = false;
            retryingMessageIds.value = new Set();
        }
    );

    watch(
        () => lastMessage.value?.id,
        (_newLastMessageId, previousLastMessageId) => {
            previousLastMessageIdBeforeUpdate = previousLastMessageId ?? null;

            const message = lastMessage.value;

            if (!message || initialLoading.value) {
                shouldScrollOnNextUpdate.value = false;
                return;
            }

            if (
                message.conversationId !== selectedConversation.value?.id ||
                message.conversationKind !== selectedConversationKind.value
            ) {
                shouldScrollOnNextUpdate.value = false;
                return;
            }

            shouldScrollOnNextUpdate.value = true;
        }
    );

    // When opened from drawer context, messages are already loaded
    // We need to scroll to bottom manually to avoid glitches with infinite loading
    // If targetCommunicationId is provided, scroll to that message instead
    watch(
        () => selectedConversationMessages.value.length,
        (length) => {
            if (!length) {
                return;
            }

            if (targetCommunicationId.value && !hasScrolledToTarget.value) {
                const targetExists = selectedConversationMessages.value.some(
                    (message) => message.communicationId === targetCommunicationId.value
                );

                if (targetExists) {
                    hasScrolledToTarget.value = true;
                    hadHistoricalUnreadAtLoad.value =
                        !!firstUnreadMessageId.value || isLastReadMessageOutsideLoadedRange.value;
                    initialLoading.value = false;
                    scrollToTargetMessage(targetCommunicationId.value);

                    return;
                }

                if (!hasFetchedAllMessages.value) {
                    // Target not found yet - scroll to top to trigger infinite loading
                    // which will fetch older messages until target is found
                    nextTick(() => {
                        const container = messagesContainer.value;
                        if (container) {
                            container.scrollTop = 0;
                        }
                    });

                    return;
                }

                // Target not found after all messages loaded, scroll to bottom as fallback
                hasScrolledToTarget.value = true;
            }

            if (initialLoading.value) {
                scrollToInitialPosition();
            }
        },
        { immediate: true }
    );

    // When targetCommunicationId changes, scroll to target if messages are already loaded
    // (e.g. maximizing drawer for same lead)
    watch(
        () => targetCommunicationId.value,
        (newId) => {
            hasScrolledToTarget.value = false;

            if (!newId) {
                return;
            }

            const targetExists = selectedConversationMessages.value.some(
                (message) => message.communicationId === newId
            );

            if (targetExists) {
                hasScrolledToTarget.value = true;
                scrollToTargetMessage(newId);
            }
        }
    );

    onMounted(() => {
        setupBottomVisibilityObserver();

        if (messagesContainer.value && typeof ResizeObserver !== 'undefined') {
            lastContainerHeight = messagesContainer.value.clientHeight;
            lastScrollHeight = messagesContainer.value.scrollHeight;
            containerResizeObserver = new ResizeObserver(() => {
                const el = messagesContainer.value;

                if (!el) {
                    return;
                }

                // Use clientHeight (content + padding) to stay consistent
                // with getIsUserAtBottom's scroll math. contentRect.height
                // excludes padding, causing a mismatch on padded containers.
                const newHeight = el.clientHeight;
                const oldHeight = lastContainerHeight;
                const oldScrollHeight = lastScrollHeight;
                lastContainerHeight = newHeight;
                lastScrollHeight = el.scrollHeight;

                // Only act when the container shrinks (window/viewport got smaller).
                // scrollTop is unchanged; use the cached scrollHeight and container
                // height from the previous observation to decide whether the user was
                // at the bottom before the shrink. Using live scrollHeight would be
                // inaccurate when text reflow changes content height during resize.
                if (oldHeight > 0 && newHeight < oldHeight) {
                    const wasAtBottom =
                        oldScrollHeight - el.scrollTop - oldHeight <= BOTTOM_THRESHOLD;

                    if (wasAtBottom) {
                        scrollToBottom();
                    }
                }
            });
            containerResizeObserver.observe(messagesContainer.value);
        }
    });
    onBeforeUnmount(() => {
        cleanupBottomVisibilityObserver();
        containerResizeObserver?.disconnect();
        clearHighlightTimer();
    });

    onBeforeUpdate(() => {
        if (!shouldScrollOnNextUpdate.value) {
            return;
        }

        wasUserAtBottom.value = getIsUserAtBottom();
    });

    onUpdated(() => {
        if (!shouldScrollOnNextUpdate.value) {
            return;
        }

        shouldScrollOnNextUpdate.value = false;

        if (!wasUserAtBottom.value) {
            newMessageIndicator.captureIncomingMessagesWhileAway(previousLastMessageIdBeforeUpdate);
            return;
        }

        scrollToBottom();

        // Re-observe sentinel to trigger a fresh visibility check.
        wasBottomSentinelVisible = false;

        if (bottomVisibilityObserver && bottomSentinel.value) {
            bottomVisibilityObserver.unobserve(bottomSentinel.value);
            bottomVisibilityObserver.observe(bottomSentinel.value);
        }
    });
</script>

<style scoped>
    .new-messages-enter-active {
        @apply transition-all duration-300;
    }
    .new-messages-leave-active {
        @apply transition-opacity duration-200;
    }
    .new-messages-enter {
        @apply opacity-0 translate-y-2;
    }
    .new-messages-leave-to {
        @apply opacity-0;
    }
</style>
